// Header (plan 8.2.2): screen name · view scope · search (Ctrl/⌘K) · help · 한|EN · theme · bell · account chip.
// Narrow widths fold it step by step in CSS (≤1439 search icon only, ≤1279 language into the account menu, ≤760 scope
// moves to the top of the content, ≤420 theme into the account menu).
import { useRef } from 'react';
import { Link } from 'react-router';
import { useDisplaySettings } from '../auth/display-settings';
import { useT } from '../i18n';
import { isMac } from '../lib/util';
import { useViewStore } from '../state/store';
import type { Lang } from '../state/url';
import { Icon } from '../ui/Icon';
import { refreshTip, toast } from '../ui/layers';
import { BellMenu, UserMenu } from './HeaderMenus';
import { HelpButton, type HelpHandle } from './Help';
import { ScopeBar } from './ScopeBar';
import { useIsDark } from './sync';

/** Theme toggle (light ↔ dark); its name says what pressing it does. Also used on the sign-in screen. */
export function ThemeButton({ className }: { className?: string }) {
  const t = useT();
  const dark = useIsDark();
  const setDisplay = useDisplaySettings();
  const ref = useRef<HTMLButtonElement>(null);
  const label = t(dark ? 'header.theme.toLight' : 'header.theme.toDark');
  return (
    <button
      ref={ref}
      className={'icon-btn theme-btn' + (className ? ' ' + className : '')}
      type="button"
      aria-label={label}
      data-tip={label}
      onClick={() => {
        const next = dark ? 'light' : 'dark';
        setDisplay({ theme: next }, () => toast(t('theme.changed', { theme: t('theme.' + next) }), 'ok'));
        requestAnimationFrame(() => refreshTip(ref.current));
      }}
    >
      <Icon name="moon" className="ic-moon" />
      <Icon name="sun" className="ic-sun" />
    </button>
  );
}

/** 한 | EN */
export function LangSeg() {
  const t = useT();
  const lang = useViewStore((s) => s.lang);
  const setDisplay = useDisplaySettings();
  return (
    <div className="segmented" role="group" aria-label={t('header.lang')}>
      {(['ko', 'en'] as Lang[]).map((lg) => (
        <button key={lg} className="segmented__option" type="button" lang={lg} aria-label={t('header.lang.' + lg)} aria-pressed={lang === lg} onClick={() => setDisplay({ lang: lg })}>
          {lg === 'ko' ? '한' : 'EN'}
        </button>
      ))}
    </div>
  );
}

export function Header({ title, onOpenPalette }: { title: string; onOpenPalette: () => void }) {
  const t = useT();
  const helpRef = useRef<HelpHandle>(null);
  return (
    <header className="app-header">
      <div className="app-header__inner">
        <Link className="app-header__mark" to="/home" aria-label={`${t('app.name')} · ${t('nav.home')}`} />
        <p className="app-header__title">{title}</p>
        <div className="app-header__actions">
          <ScopeBar where="header" />
          <button className="cmd-trigger" type="button" aria-label={t('header.searchLabel')} aria-haspopup="dialog" data-tip={t('header.searchLabel')} data-tip-when="icon-only" onClick={onOpenPalette}>
            <Icon name="search" />
            <span className="cmd-trigger__text">{t('header.search')}</span>
            <kbd className="kbd">{isMac() ? '⌘ K' : 'Ctrl K'}</kbd>
          </button>
          <HelpButton ref={helpRef} />
          <LangSeg />
          <ThemeButton />
          <BellMenu />
          <UserMenu onOpenHelp={() => helpRef.current?.open()} />
        </div>
      </div>
    </header>
  );
}
